import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, CheckCircle, XCircle, ExternalLink, Calendar, TrendingUp, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import { approveDraft, rejectDraft } from '@/services/draftApprovalService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Draft {
  id: string;
  title: string;
  region: string;
  status: string;
  content: string;
  source_articles: any[];
  event_clusters: any[];
  composite_score: number;
  created_at: string;
  published_at: string | null;
  rejection_reason: string | null;
  generation_metadata: any;
}

interface DraftDetailViewProps {
  draft: Draft;
  onClose: () => void;
  onUpdate: () => void;
}

export const DraftDetailView: React.FC<DraftDetailViewProps> = ({ draft, onClose, onUpdate }) => {
  const { toast } = useToast();
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      await approveDraft(draft.id, draft);
      toast({
        title: 'Artikkel godkjent og publisert',
        description: `"${draft.title}" er nå publisert på Briefr.no`,
      });
      onUpdate();
    } catch (error: any) {
      toast({
        title: 'Feil ved godkjenning',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast({
        title: 'Mangler grunn',
        description: 'Du må oppgi en grunn for avvisning',
        variant: 'destructive',
      });
      return;
    }

    setIsRejecting(true);
    try {
      await rejectDraft(draft.id, rejectionReason);
      toast({
        title: 'Artikkel avvist',
        description: `"${draft.title}" er markert som avvist`,
      });
      setShowRejectDialog(false);
      onUpdate();
    } catch (error: any) {
      toast({
        title: 'Feil ved avvisning',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsRejecting(false);
    }
  };

  const getRegionDisplayName = (region: string) => {
    const mapping: Record<string, string> = {
      europa: 'Europa',
      nord_amerika: 'Nord-Amerika',
      asia: 'Asia',
      latin_amerika: 'Latin-Amerika',
      midtosten: 'Midtøsten',
      afrika: 'Afrika',
      skandinavia: 'Skandinavia',
    };
    return mapping[region] || region;
  };

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={onClose}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Tilbake til liste
        </Button>
        <Badge variant={draft.status === 'draft' ? 'secondary' : 'default'}>
          {draft.status}
        </Badge>
      </div>

      {/* Main Content Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-2xl">{draft.title}</CardTitle>
              <CardDescription className="mt-2 space-x-3">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {format(new Date(draft.created_at), 'PPP HH:mm', { locale: nb })}
                </span>
                <span className="inline-flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" />
                  {getRegionDisplayName(draft.region)}
                </span>
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Content */}
          <div>
            <h3 className="font-semibold mb-3">Artikkelinnhold</h3>
            <div className="prose prose-sm max-w-none bg-muted/30 p-4 rounded-lg">
              <pre className="whitespace-pre-wrap font-sans text-sm">{draft.content}</pre>
            </div>
          </div>

          <Separator />

          {/* Sources */}
          <div>
            <h3 className="font-semibold mb-3">
              Kilder ({draft.source_articles?.length || 0})
            </h3>
            <div className="space-y-2">
              {draft.source_articles?.map((source: any, idx: number) => (
                <Card key={idx} className="bg-muted/30">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{source.title}</h4>
                        <p className="text-xs text-muted-foreground mt-1">{source.domain}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                      >
                        <a href={source.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <Separator />

          {/* Event Clusters */}
          {draft.event_clusters && draft.event_clusters.length > 0 && (
            <>
              <div>
                <h3 className="font-semibold mb-3">
                  Hendelsesklynger ({draft.event_clusters.length})
                </h3>
                <div className="space-y-3">
                  {draft.event_clusters.map((cluster: any, idx: number) => (
                    <Card key={idx} className="bg-muted/30">
                      <CardContent className="p-4">
                        <h4 className="font-medium text-sm mb-2">{cluster.title || `Klynge ${idx + 1}`}</h4>
                        <div className="text-xs text-muted-foreground space-y-1">
                          {cluster.sources?.map((src: any, srcIdx: number) => (
                            <div key={srcIdx} className="flex items-center gap-2">
                              <span className="font-mono">{src.domain}</span>
                              <span>•</span>
                              <span className="truncate">{src.title}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Metadata */}
          <div>
            <h3 className="font-semibold mb-3">Genererings-metadata</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Composite Score:</span>
                <span className="ml-2 font-medium">{draft.composite_score.toFixed(3)}</span>
              </div>
              {draft.generation_metadata?.model && (
                <div>
                  <span className="text-muted-foreground">Modell:</span>
                  <span className="ml-2 font-medium">{draft.generation_metadata.model}</span>
                </div>
              )}
              {draft.generation_metadata?.temperature !== undefined && (
                <div>
                  <span className="text-muted-foreground">Temperature:</span>
                  <span className="ml-2 font-medium">{draft.generation_metadata.temperature}</span>
                </div>
              )}
              {draft.generation_metadata?.promptTokens && (
                <div>
                  <span className="text-muted-foreground">Prompt Tokens:</span>
                  <span className="ml-2 font-medium">{draft.generation_metadata.promptTokens}</span>
                </div>
              )}
              {draft.generation_metadata?.completionTokens && (
                <div>
                  <span className="text-muted-foreground">Completion Tokens:</span>
                  <span className="ml-2 font-medium">{draft.generation_metadata.completionTokens}</span>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Action Buttons */}
          {draft.status === 'draft' && (
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="destructive"
                onClick={() => setShowRejectDialog(true)}
                disabled={isApproving || isRejecting}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Avvis artikkel
              </Button>
              <Button
                variant="default"
                onClick={handleApprove}
                disabled={isApproving || isRejecting}
              >
                {isApproving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Godkjenner...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Godkjenn og publiser
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Avvis artikkel</AlertDialogTitle>
            <AlertDialogDescription>
              Oppgi en grunn for hvorfor denne artikkelen avvises. Dette vil bli lagret for fremtidig referanse.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <Textarea
              placeholder="Skriv inn grunn for avvisning..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRejecting}>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleReject} disabled={isRejecting || !rejectionReason.trim()}>
              {isRejecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Avviser...
                </>
              ) : (
                'Bekreft avvisning'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
